import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { useStore } from '@base-ui/utils/store/useStore';
import { schedulerEventSelectors } from "../../scheduler-selectors/index.mjs";
import { useEventCalendarStoreContext } from "../../use-event-calendar-store-context/index.mjs";
import { useCalendarGridDayRowContext } from "../day-row/CalendarGridDayRowContext.mjs";
import { useAdapterContext } from "../../use-adapter-context/index.mjs";
import { eventCalendarOccurrencePlaceholderSelectors } from "../../event-calendar-selectors/index.mjs";
import { processDate } from "../../process-date/index.mjs";
import { isInternalDragOrResizePlaceholder } from "../../internals/utils/drag-utils.mjs";
export function useCalendarGridPlaceholderInDay(day, row, maxEvents) {
  const adapter = useAdapterContext();
  const store = useEventCalendarStoreContext();
  const {
    start: rowStart,
    end: rowEnd
  } = useCalendarGridDayRowContext();
  const rawPlaceholder = useStore(store, eventCalendarOccurrencePlaceholderSelectors.placeholderInDayCell, day, rowStart);
  const originalEventId = isInternalDragOrResizePlaceholder(rawPlaceholder) ? rawPlaceholder.eventId : null;
  const originalEvent = useStore(store, schedulerEventSelectors.processedEvent, originalEventId);
  return React.useMemo(() => {
    if (!rawPlaceholder) {
      return null;
    }
    const sharedProperties = {
      key: 'occurrence-placeholder',
      id: originalEventId ?? 'occurrence-placeholder',
      title: originalEvent ? originalEvent.title : ''
    };

    // Creation mode
    if (rawPlaceholder.type === 'creation') {
      const startProcessed = processDate(day, adapter);
      const endProcessed = processDate(adapter.isAfter(rawPlaceholder.end, rowEnd) ? rowEnd : rawPlaceholder.end, adapter);
      const timezone = adapter.getTimezone(day);
      return _extends({}, sharedProperties, {
        title: '',
        allDay: true,
        displayTimezone: {
          start: startProcessed,
          end: endProcessed,
          timezone
        },
        position: {
          index: 1,
          daySpan: adapter.differenceInDays(rawPlaceholder.end, day) + 1
        }
      });
    }
    if (rawPlaceholder.type === 'external-drag') {
      const startProcessed = processDate(rawPlaceholder.start, adapter);
      const endProcessed = processDate(rawPlaceholder.end, adapter);
      const timezone = adapter.getTimezone(rawPlaceholder.start);
      return _extends({}, sharedProperties, {
        title: rawPlaceholder.eventData.title ?? '',
        displayTimezone: {
          start: startProcessed,
          end: endProcessed,
          timezone
        },
        position: {
          index: 1,
          daySpan: adapter.differenceInDays(rawPlaceholder.end, day) + 1
        }
      });
    }
    let positionIndex = 1;
    const targetDay = row.days.find(rowDay => adapter.isSameDay(rowDay.value, day));
    if (targetDay) {
      const usedIndexes = new Set(targetDay.withPosition.filter(occ => occ.key !== rawPlaceholder.occurrenceKey).map(occ => occ.position.index));
      while (usedIndexes.has(positionIndex)) {
        positionIndex += 1;
      }
    }

    // If the position exceeds the available event rows, clamp it so the
    // placeholder renders on top of an existing event instead of overflowing.
    if (maxEvents != null && positionIndex > maxEvents) {
      positionIndex = maxEvents;
    }
    return _extends({}, sharedProperties, {
      start: processDate(rawPlaceholder.start, adapter),
      end: processDate(rawPlaceholder.end, adapter),
      displayTimezone: _extends({}, originalEvent.displayTimezone),
      position: {
        index: positionIndex,
        daySpan: adapter.differenceInDays(rawPlaceholder.end, day) + 1
      }
    });
  }, [adapter, day, maxEvents, originalEvent, originalEventId, rawPlaceholder, row.days, rowEnd]);
}